<template>
  <div class="foodleap-transition-landing antialiased" v-if="transition">

    <!-- ───────────────────────────── 1. Hero ───────────────────────────── -->
    <header class="ftl-hero relative pt-32 pb-16 overflow-hidden">
      <div class="max-w-screen-2xl mx-auto px-8 relative z-10">
        <div class="tw-flex items-center gap-3 mb-6 animate-on-scroll">
          <a @click="$router.go(-1)" class="tw-flex items-center gap-2 text-xs font-bold text-primary-slate/40 hover:text-primary-slate/70 transition-colors">
            <Icon name="arrow-left" size="12"/>
            Go Back
          </a>
        </div>

        <div class="tw-flex flex-col lg:flex-row lg:items-start gap-12">
          <!-- Left: title + intro + CTAs -->
          <div class="flex-1 min-w-0">
            <div class="tw-flex items-center gap-3 mb-5 animate-on-scroll">
              <span class="ftl-num text-xs font-black text-primary-slate/20">{{ String(transitionIndex + 1).padStart(2, '0') }}</span>
              <div class="tw-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase" :class="transition.pillClass">
                <Icon :name="transition.icon" size="12"/>
                {{ transition.label }}
              </div>
            </div>
            <h1 class="text-5xl md:text-7xl font-bold tracking-tight text-primary-slate mb-6 leading-[1.1] animate-on-scroll delay-1">
              {{ transition.title }}
            </h1>
            <p class="text-xl text-primary-slate/60 font-light max-w-2xl animate-on-scroll delay-2">
              {{ transition.description }}
            </p>

            <div class="tw-flex flex-wrap gap-3 mt-10 animate-on-scroll delay-2">
              <button
                @click="scrollTo('mapSection')"
                class="tw-inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-bold transition-all active:scale-95"
                :class="transition.ctaBtnClass">
                <Icon name="view-map" size="14"/>
                Explore Ecosystem Map
              </button>
              <button
                @click="scrollTo('insightsSection')"
                class="ftl-btn-ghost tw-inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-bold">
                <Icon name="analysis" size="14"/>
                View Latest Insights
              </button>
            </div>
          </div>

          <!-- Right: stat panel -->
          <div class="ftl-stat-panel shrink-0 w-full lg:w-[380px] rounded-2xl border p-6 animate-on-scroll delay-2" :class="transition.detailBorderClass">
            <div class="grid grid-cols-2 gap-x-6 gap-y-7">
              <div v-for="(stat, si) in heroStats" :key="si" class="tw-flex items-start gap-3">
                <div class="w-10 h-10 rounded-xl tw-flex items-center justify-center shrink-0" :class="transition.pillClass">
                  <Icon :name="stat.icon" size="16"/>
                </div>
                <div>
                  <div class="text-2xl font-black text-primary-slate leading-none">{{ stat.value }}</div>
                  <div class="text-xs text-primary-slate/50 font-medium mt-1.5 leading-tight">{{ stat.label }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="ftl-blob absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl opacity-20" :class="transition.blobClass"></div>
      <div class="ftl-blob-2 absolute -bottom-24 -left-24 w-80 h-80 rounded-full blur-3xl opacity-15"></div>
    </header>

    <!-- ──────────────────── 2. Introduction to the transition ──────────────────── -->
    <section class="max-w-screen-2xl mx-auto px-8 py-16">
      <div class="ftl-intro-card rounded-2xl border p-8 md:p-12 animate-on-scroll" :class="transition.detailBorderClass">
        <h2 class="text-2xl md:text-3xl font-bold text-primary-slate mb-8">{{ transition.intro.heading }}</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <p
            v-for="(para, pi) in transition.intro.paragraphs"
            :key="pi"
            class="text-base text-primary-slate/60 font-light leading-relaxed">
            {{ para }}
          </p>
        </div>
      </div>
    </section>

    <!-- ──────────────────── 3. Interactive Ecosystem Map ──────────────────── -->
    <section ref="mapSection" class="mx-auto px-8 pb-10 scroll-mt-24">
      <!--      <div class="tw-flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-8 animate-on-scroll">
              <div>
                <h2 class="text-2xl md:text-3xl font-bold text-primary-slate mb-3">{{ transition.title }} Ecosystem Map</h2>
                <div class="tw-flex flex-wrap gap-2">
                  <span
                    v-for="(f, fi) in transition.features"
                    :key="fi"
                    class="text-xs font-semibold px-3 py-1.5 rounded-full"
                    :class="transition.pillClass">
                    {{ f }}
                  </span>
                </div>
              </div>
              <button
                @click="onAddOrganisation"
                class="shrink-0 px-8 py-3 rounded-full text-sm font-bold transition-all active:scale-95"
                :class="transition.ctaBtnClass">
                Add your organisation
              </button>
            </div>-->

      <div class="ftl-map-wrap relative w-full rounded-2xl overflow-hidden border animate-on-scroll" :class="transition.detailBorderClass" style="height: 100vh;">
        <iframe v-if="!transition.mapComingSoon" class="w-full h-full border-none" :src="transition.mapUrl" allowfullscreen allow="geolocation"/>
        <!-- Coming soon overlay -->
        <div
          v-if="transition.mapComingSoon"
          class="absolute inset-0 tw-flex flex-col items-center justify-center gap-5"
          style="backdrop-filter:blur(8px);background:rgba(255,255,255,0.7);">
          <div class="w-14 h-14 rounded-2xl tw-flex items-center justify-center" :class="transition.iconBgActive">
            <Icon :name="transition.icon" size="24" :class="transition.iconColorActive"/>
          </div>
          <div class="text-center">
            <p class="font-bold text-primary-slate text-lg mb-2">{{ transition.label }} map — coming soon</p>
            <p class="text-sm text-primary-slate/50 font-light max-w-xs leading-relaxed">
              We're building out this transition map. Be among the first to add your organisation.
            </p>
          </div>
          <button @click="onAddOrganisation" class="px-8 py-3 rounded-full text-sm font-bold active:scale-95" :class="transition.ctaBtnClass">
            Add your organisation
          </button>
        </div>
      </div>
    </section>

    <!-- ──────────────────── 4. Ecosystem Facts & Figures ──────────────────── -->
    <section class="max-w-screen-2xl mx-auto px-8 py-16">
      <div class="mb-8 animate-on-scroll">
        <h2 class="text-2xl md:text-3xl font-bold text-primary-slate mb-2">Ecosystem Facts &amp; Figures</h2>
        <p class="text-sm text-primary-slate/50 font-light">A live snapshot of the organisations, signals and connections we track in this transition.</p>
      </div>
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div
          v-for="(fact, fi) in transition.facts"
          :key="fi"
          class="ftl-fact-card rounded-xl border p-5 animate-on-scroll"
          :class="transition.detailBorderClass">
          <div class="w-10 h-10 rounded-xl tw-flex items-center justify-center mb-4" :class="transition.pillClass">
            <Icon :name="fact.icon" size="16"/>
          </div>
          <div class="text-2xl font-black text-primary-slate leading-none">{{ fact.value }}</div>
          <div class="text-xs text-primary-slate/50 font-medium mt-2 leading-tight">{{ fact.label }}</div>
        </div>
      </div>
    </section>

    <!-- ──────────────────── 5. Key Innovation Areas ──────────────────── -->
    <section class="max-w-screen-2xl mx-auto px-8 pb-16">
      <div class="mb-8 animate-on-scroll">
        <h2 class="text-2xl md:text-3xl font-bold text-primary-slate mb-2">Key Innovation Areas</h2>
        <p class="text-sm text-primary-slate/50 font-light">The domains where innovation is concentrating across this transition.</p>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          v-for="(area, ai) in transition.innovationAreas"
          :key="ai"
          class="ftl-area-row tw-flex items-center gap-4 rounded-xl border p-5 animate-on-scroll"
          :class="transition.detailBorderClass">
          <div class="w-11 h-11 rounded-xl tw-flex items-center justify-center shrink-0" :class="transition.pillClass">
            <Icon :name="area.icon" size="18"/>
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-bold text-primary-slate">{{ area.label }}</div>
            <div class="text-xs text-primary-slate/50 font-light mt-0.5">{{ area.desc }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ──────────────────── 6. Foodleap Insights ──────────────────── -->
    <section ref="insightsSection" class="max-w-screen-2xl mx-auto px-8 py-16 scroll-mt-24">
      <div class="mb-8 animate-on-scroll">
        <h2 class="text-2xl md:text-3xl font-bold text-primary-slate mb-2">Foodleap Insights</h2>
        <p class="text-sm text-primary-slate/50 font-light">Pulses, radars and deep dives from the {{ transition.label }} transition.</p>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div
          v-for="(insight, ii) in transition.insights"
          :key="ii"
          @click="onInsightClick(insight)"
          class="ftl-insight-card group cursor-pointer tw-flex flex-col rounded-xl border p-6 bg-white animate-on-scroll"
          :class="transition.detailBorderClass">
          <div class="tw-flex items-center gap-2 mb-4 text-[10px] font-bold tracking-widest uppercase" :class="transition.labelColor">
            <Icon :name="insight.icon" size="12"/>
            {{ insight.kind }}
          </div>
          <h3 class="text-base font-bold text-primary-slate leading-snug mb-3">{{ insight.title }}</h3>
          <p class="text-xs text-primary-slate/50 font-light leading-relaxed mb-5 flex-1">{{ insight.summary }}</p>
          <div class="tw-flex items-center mt-auto">
            <span class="tw-inline-flex items-center gap-1 text-xs font-bold" :class="transition.linkColor">
              Read more
              <Icon name="arrow-right" size="12" class="transition-transform group-hover:translate-x-1"/>
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- ──────────────────── 7. Ecosystem Partners ──────────────────── -->
    <section class="max-w-screen-2xl mx-auto px-8 pb-16">
      <div class="ftl-partners-card rounded-2xl border p-8 md:p-12 animate-on-scroll" :class="transition.detailBorderClass">
        <div class="max-w-2xl mb-10">
          <h2 class="text-2xl md:text-3xl font-bold text-primary-slate mb-3">Ecosystem Partners</h2>
          <p class="text-sm text-primary-slate/50 font-light leading-relaxed">
            Foodleap works together with leading organisations to strengthen and grow the {{ transition.label }} transition ecosystem.
          </p>
        </div>
        <!-- Partner actors carousel (from the transition's partner portfolio) -->
        <Carousel
          v-if="partnerActors.length"
          :key="'partners-carousel-' + slug"
          :settings="carouselSettings(3)"
          :breakpoints="carouselBreakpoints(3)"
          class="ftl-partners-carousel"
        >
          <Slide v-for="actor in partnerActors" :key="actor.id">
            <div class="tw-flex w-full px-2">
              <SamergoActorCard
                :actor="actor"
                :contributors="actor.contributors || []"
                :claimed-actors="[]"
                :description="actor.short_description || actor.description"
                read-only
                hide-contact
                style="width: 100%"
              />
            </div>
          </Slide>
          <template #addons>
            <Navigation v-if="partnerActors.length > 3"/>
          </template>
        </Carousel>

        <!-- Fallback: partner name chips while the portfolio has no actors -->
        <div v-else class="tw-flex flex-wrap items-center gap-x-10 gap-y-6">
          <span
            v-for="(partner, pi) in transition.partners"
            :key="pi"
            class="text-lg font-black text-primary-slate/30 hover:text-primary-slate/60 transition-colors">
            {{ partner }}
          </span>
        </div>
        <button @click="onBecomePartner" class="ftl-btn-ghost tw-inline-flex items-center gap-2 px-7 py-3 rounded-full text-sm font-bold mt-10">
          Become a partner
          <Icon name="arrow-right" size="14"/>
        </button>
      </div>
    </section>

    <!-- ──────────────────── 8. FAQ ──────────────────── -->
    <section class="max-w-3xl mx-auto px-8 py-16">
      <div class="mb-8 animate-on-scroll">
        <h2 class="text-2xl md:text-3xl font-bold text-primary-slate mb-2">Frequently Asked Questions</h2>
        <p class="text-sm text-primary-slate/50 font-light">Everything you need to know about the {{ transition.label }} transition on Foodleap.</p>
      </div>
      <div class="animate-on-scroll">
        <div
          v-for="(item, fi) in transition.faq"
          :key="fi"
          class="ftl-faq-item border-b"
          style="border-color: #D9E2DA;">
          <button @click="toggleFaq(fi)" class="w-full tw-flex items-center justify-between gap-4 py-5 text-left">
            <span class="text-sm font-bold text-primary-slate">{{ item.q }}</span>
            <Icon :name="openFaq === fi ? 'minus' : 'plus'" size="14" class="shrink-0" :class="transition.linkColor"/>
          </button>
          <div v-if="openFaq === fi" class="pb-5 -mt-1 text-sm text-primary-slate/60 font-light leading-relaxed">
            {{ item.a }}
          </div>
        </div>
      </div>
    </section>

    <!-- Other transitions -->
    <section class="max-w-screen-2xl mx-auto px-8 py-16 border-t" style="border-color: #D9E2DA;">
      <div class="mb-8 animate-on-scroll">
        <h2 class="text-2xl font-bold text-primary-slate mb-2">Explore the other transitions</h2>
        <p class="text-sm text-primary-slate/50 font-light">The food system is shifting along several distinct axes.</p>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <router-link
          v-for="(t, i) in otherTransitions"
          :key="i"
          :to="'/transitions/' + t.slug"
          class="ftl-other-card group rounded-xl overflow-hidden border transition-all bg-white no-underline tw-flex flex-col"
          :class="t.borderClass">
          <!-- Visual header -->
          <div class="relative h-52 overflow-hidden" :class="t.headerBg">
            <img :src="t.image" :alt="t.label" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"/>
          </div>
          <!-- Content -->
          <div class="p-5 border-t tw-flex flex-col flex-1" :class="t.dividerClass">
            <div class="text-[11px] font-bold tracking-widest uppercase mb-2" :class="t.labelColor">{{ t.label }}</div>
            <h3 class="text-lg font-bold text-primary-slate leading-tight mb-2.5">{{ t.title }}</h3>
            <p class="text-sm text-primary-slate/50 font-light leading-snug">{{ t.desc }}</p>
            <!-- Facts & figures -->
            <div v-if="t.cardFacts && t.cardFacts.length" class="mt-auto pt-4 border-t grid grid-cols-3" :class="t.dividerClass">
              <div
                v-for="(fact, fi) in t.cardFacts"
                :key="fi"
                class="tw-flex flex-col items-center justify-center text-center px-2 min-w-0"
                :class="fi > 0 ? 'border-l ' + t.dividerClass : ''">
                <span class="text-xl font-black text-primary-slate leading-none">{{ fact.value }}</span>
                <span class="w-full truncate text-[10px] text-primary-slate/40 font-medium uppercase mt-2 leading-tight">{{ fact.label }}</span>
              </div>
            </div>
            <div class="mt-5 tw-flex items-center gap-1.5 text-sm font-bold" :class="t.linkColor">
              Explore map
              <Icon name="arrow-right" size="14" class="transition-transform group-hover:translate-x-1"/>
            </div>
          </div>
        </router-link>
      </div>
    </section>

    <!-- Bottom CTA -->
    <section class="py-24 ftl-cta-bg">
      <div class="max-w-3xl mx-auto px-8 text-center animate-on-scroll">
        <h2 class="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight">
          Be part of the {{ transition.label }} transition
        </h2>
        <p class="text-white/60 text-xl font-light mb-10">
          Join the organisations building a more sustainable food system — add your organisation or talk to our team.
        </p>
        <div class="tw-flex flex-wrap justify-center gap-4">
          <button
            @click="onAddOrganisation"
            class="ftl-btn-green tw-inline-flex items-center gap-2 px-10 py-4 rounded-full font-bold text-lg">
            Add your organisation
            <Icon name="arrow-right" size="16"/>
          </button>
          <button
            @click="$router.push('/transitions')"
            class="ftl-btn-outline px-10 py-4 rounded-full font-bold text-lg">
            View all transitions
          </button>
        </div>
      </div>
    </section>

    <SimplifiedFooterFoodleapNew/>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'
  import { Carousel, Navigation, Slide } from 'vue3-carousel'
  import SimplifiedFooterFoodleapNew from '@/components/SimplifiedFooter/SimplifiedFooterFoodleapNew.vue'
  import SamergoActorCard from '@/components/Samergo/SamergoActorCard.vue'
  import Icon from '../Icon/Icon.vue'
  import CarouselMixin from '@/util/CarouselMixin'
  import { fetchActors } from '@/api/actors'
  import { createScrollDepthTracker, trackFoodleapCta } from '@/util/useFoodleapTracking.js'

  const PARTNERS_LIMIT = 24

  const ALL_TRANSITIONS = [
    {
      slug: 'protein',
      label: 'Protein',
      shortTitle: 'Novel & alternative proteins',
      title: 'Protein Transition',
      description: 'Explore the organisations, innovations and insights driving the shift towards a more diverse, resilient and sustainable protein system. Discover the ecosystem shaping one of the most significant transitions in the future of food.',
      shortDesc: 'Explore the organisations, innovations and insights driving the protein transition.',
      icon: 'vial',
      intro: {
        heading: 'Introduction',
        paragraphs: [
          'The Protein Transition is transforming how the world produces, processes and consumes protein.',
          'Driven by sustainability challenges, food security concerns, changing consumer preferences and technological innovation, organisations across the food system are exploring new approaches to protein production and consumption.',
          'The transition spans a broad ecosystem of startups, food companies, ingredient suppliers, researchers, investors, policymakers and ecosystem organisations working together to build a more resilient food system.',
        ],
      },
      stats: [
        { value: '1,248', label: 'organisations' },
        { value: '43', label: 'countries' },
        { value: '72', label: 'domains' },
      ],
      facts: [
        { icon: 'users', value: '1,248', label: 'organisations' },
        { icon: 'circle-nodes', value: '14', label: 'stakeholder groups' },
        { icon: 'globe', value: '43', label: 'countries' },
        { icon: 'light-bulb-solid', value: '72', label: 'domains' },
      ],
      // Key Innovation Areas — segmentation per Confluence (check definition with Andres)
      innovationAreas: [
        { icon: 'eco', label: 'Alternative Proteins', desc: 'Plant-based & novel protein products' },
        { icon: 'lab', label: 'Precision Fermentation', desc: 'Microbial production of functional proteins' },
        { icon: 'wave-square', label: 'Biomass Fermentation', desc: 'Mycoprotein & microbial biomass' },
        { icon: 'light-bulb-solid', label: 'Protein Ingredients', desc: 'Functional proteins, textures & isolates' },
        { icon: 'chart-bubble', label: 'Hybrid Products', desc: 'Blended animal & plant formats' },
        { icon: 'bar-chart', label: 'Protein Crops', desc: 'New & upgraded plant protein sources' },
        { icon: 'vial', label: 'Food Processing Technologies', desc: 'Extrusion, texturisation & processing' },
      ],
      insights: [
        {
          kind: 'Pulse',
          icon: 'wave-square',
          title: 'Monthly snapshot of the transition',
          summary: 'A regular pulse on the signals, momentum and shifts shaping the protein transition.',
          meta: 'View →'
        },
        {
          kind: 'Radar',
          icon: 'chart-bubble',
          title: 'Emerging players and trends',
          summary: 'Discover the emerging organisations and innovations shaping the protein ecosystem.',
          meta: 'View →'
        },
        {
          kind: 'Deep Dive',
          icon: 'news-paper-solid',
          title: 'In-depth protein transition reports',
          summary: 'Deep dive analyses exploring the technologies and dynamics driving the transition.',
          meta: 'Read reports'
        },
      ],
      partners: ['Foodvalley'],
      // TODO: set the partner portfolio id for the Protein transition
      partnersPortfolio: 'K0xLaQWVQO',
      faq: [
        {
          q: 'What is the protein transition?',
          a: 'The protein transition is the global shift towards a more diverse, resilient and sustainable protein system — spanning new sources, formats and production methods alongside conventional protein.'
        },
        {
          q: 'Why is the Protein Transition important?',
          a: 'It responds to sustainability challenges, food security concerns and changing consumer preferences, helping build a more resilient and future-proof food system.'
        },
        {
          q: 'What are alternative proteins?',
          a: 'Alternative proteins are protein sources beyond conventional animal agriculture, including plant-based, fermentation-derived and hybrid products.'
        },
        {
          q: 'What is precision fermentation?',
          a: 'Precision fermentation uses micro-organisms programmed to produce specific functional ingredients such as proteins, fats and enzymes.'
        },
        {
          q: 'Which organisations are active in the Protein Transition?',
          a: 'The ecosystem includes startups, food companies, ingredient suppliers, researchers, investors, policymakers and ecosystem organisations working across the protein value chain.'
        },
        {
          q: 'How can I identify relevant partners and collaborators?',
          a: 'Use the interactive ecosystem map to explore organisations by activity and domain, or register to connect with relevant partners and collaborators.'
        },
      ],
      mapUrl: 'https://platform.foodleap.com/ecosystem-maps/15/embed?apiKey=ds_live_LTesBOBYi8scwskp2etM',
      mapComingSoon: false,
      image: '/images/static-home-page/foodleap/transition-maps/protein.png',
      blobClass: 'ftl-blob-purple',
      pillClass: 'bg-secondary-purple/10 text-secondary-purple',
      ctaBtnClass: 'bg-secondary-purple text-white hover:opacity-90',
      detailBorderClass: 'border-secondary-purple/20',
      iconBgActive: 'bg-secondary-purple/20',
      iconColorActive: 'text-secondary-purple',
      headerBgClass: 'bg-secondary-purple/5',
      borderClass: 'border-secondary-purple/20 hover:border-secondary-purple/50',
      dividerClass: 'border-secondary-purple/10',
      labelColor: 'text-secondary-purple',
      linkColor: 'text-secondary-purple',
    },
    {
      slug: 'circular',
      label: 'Circular',
      shortTitle: 'Upcycling & resource efficiency',
      title: 'Food Upcycling',
      description: 'Explore the organisations, innovations and insights helping food systems use resources more efficiently and create value from side streams. Discover the ecosystem driving the transition towards more circular and resource efficient food systems.',
      shortDesc: 'Explore the organisations and innovations helping food systems use resources more efficiently.',
      icon: 'sync',
      intro: {
        heading: 'Introduction',
        paragraphs: [
          'Food systems worldwide face increasing pressure to reduce waste, improve resource efficiency and maximise the value of available resources.',
          'The Food Upcycling transition focuses on recovering value from side streams, reducing losses and enabling more circular production and processing models.',
          'The ecosystem includes food manufacturers, ingredient companies, technology providers, researchers, investors and ecosystem organisations working together to build more resilient and resource-efficient food systems.',
        ],
      },
      stats: [
        { value: '140+', label: 'organisations' },
        { value: '12', label: 'countries' },
        { value: '18', label: 'domains' },
      ],
      facts: [
        { icon: 'users', value: '140+', label: 'organisations' },
        { icon: 'circle-nodes', value: '7', label: 'stakeholder groups' },
        { icon: 'light-bulb-solid', value: '18', label: 'innovation domains' },
        { icon: 'globe', value: '12', label: 'countries' },
        { icon: 'wave-square', value: '8,200+', label: 'signals monitored' },
      ],
      innovationAreas: [
        { icon: 'sync', label: 'Upcycling', desc: 'Value from food side streams' },
        { icon: 'wave-square', label: 'Side Stream Valorisation', desc: 'Recovering value from by-products' },
        { icon: 'eco', label: 'Circular Ingredients', desc: 'Ingredients from recovered streams' },
        { icon: 'bar-chart', label: 'Waste Reduction', desc: 'Cutting losses across the chain' },
        { icon: 'light-bulb-solid', label: 'Resource Efficiency', desc: 'Water, energy & input reduction' },
        { icon: 'chart-bubble', label: 'Circular Supply Chains', desc: 'Closed-loop sourcing & logistics' },
        { icon: 'vial', label: 'Food Processing Technologies', desc: 'Processing for circular production' },
      ],
      insights: [
        {
          kind: 'Pulse',
          icon: 'wave-square',
          title: 'Monthly snapshot of the transition',
          summary: 'A regular pulse on the signals and momentum shaping circular, resource-efficient food systems.',
          meta: 'View →'
        },
        {
          kind: 'Radar',
          icon: 'chart-bubble',
          title: 'Emerging players and trends',
          summary: 'Discover the emerging organisations and innovations driving food upcycling and circularity.',
          meta: 'View →'
        },
        {
          kind: 'Deep Dive',
          icon: 'news-paper-solid',
          title: 'In-depth circular food reports',
          summary: 'Deep dive analyses exploring upcycling, valorisation and resource efficiency.',
          meta: 'Read reports'
        },
      ],
      partners: ['Foodvalley', 'DKI', 'Upcycled Food Association'],
      // TODO: set the partner portfolio id for the Circular transition
      partnersPortfolio: 'L0V4vEWkb7',
      faq: [
        {
          q: 'What is food upcycling?',
          a: 'Food upcycling turns food side streams and by-products that would otherwise go to waste into higher-value ingredients and products.'
        },
        {
          q: 'How can side streams be valorised?',
          a: 'Side streams can be valorised through technologies that recover proteins, fibres, bioactives and other components for use in new food, feed or materials.'
        },
        {
          q: 'Which organisations are active in this transition?',
          a: 'The ecosystem includes food manufacturers, ingredient companies, technology providers, researchers, investors and ecosystem organisations.'
        },
        {
          q: 'What technologies enable circular food systems?',
          a: 'Extraction, fermentation, separation and processing technologies enable the recovery, valorisation and reuse of resources across the food chain.'
        },
      ],
      mapUrl: 'https://platform.foodleap.com/ecosystem-maps/4/embed?apiKey=ds_live_iM84TiIL8KwvRrsnZuKq',
      mapComingSoon: false,
      image: '/images/static-home-page/foodleap/transition-maps/circular.png',
      blobClass: 'ftl-blob-blue',
      pillClass: 'bg-secondary-blue/10 text-secondary-blue',
      ctaBtnClass: 'bg-secondary-blue text-primary-slate hover:opacity-90',
      detailBorderClass: 'border-secondary-blue/20',
      iconBgActive: 'bg-secondary-blue/20',
      iconColorActive: 'text-secondary-blue',
      headerBgClass: 'bg-secondary-blue/5',
      borderClass: 'border-secondary-blue/20 hover:border-secondary-blue/50',
      dividerClass: 'border-secondary-blue/10',
      labelColor: 'text-secondary-blue',
      linkColor: 'text-secondary-blue',
    },
    {
      slug: 'food-infrastructure',
      label: 'Health',
      shortTitle: 'Nutrition, prevention & health',
      title: 'Health & Functional Food',
      description: 'Explore the organisations, innovations and insights at the intersection of food, nutrition and health. Discover the ecosystem contributing to healthier and more preventative food systems.',
      shortDesc: 'Explore the organisations and innovations at the intersection of food, nutrition and health.',
      icon: 'building',
      intro: {
        heading: 'Introduction',
        paragraphs: [
          'Growing healthcare challenges, ageing populations and increasing scientific understanding of nutrition are driving a shift towards more health-oriented food systems.',
          'The Healthy & Functional Food transition focuses on improving wellbeing through nutrition, supporting prevention and enabling consumers to make healthier choices.',
          'The ecosystem spans food companies, ingredient suppliers, healthcare organisations, biotech innovators, researchers, investors and ecosystem builders working at the intersection of food and health.',
        ],
      },
      stats: [
        { value: '120+', label: 'organisations' },
        { value: '14', label: 'countries' },
        { value: '16', label: 'domains' },
      ],
      facts: [
        { icon: 'users', value: '120+', label: 'organisations' },
        { icon: 'circle-nodes', value: '6', label: 'stakeholder groups' },
        { icon: 'light-bulb-solid', value: '16', label: 'innovation domains' },
        { icon: 'globe', value: '14', label: 'countries' },
        { icon: 'wave-square', value: '6,500+', label: 'signals monitored' },
      ],
      innovationAreas: [
        { icon: 'lab', label: 'Functional Ingredients', desc: 'Bioactives, fibres & probiotics' },
        { icon: 'wave-square', label: 'Personalised Nutrition', desc: 'Data-driven dietary guidance' },
        { icon: 'chart-bubble', label: 'Gut Health & Microbiome', desc: 'Microbiome & digestive health' },
        { icon: 'bar-chart', label: 'Metabolic Health', desc: 'Glycaemic & metabolic solutions' },
        { icon: 'eco', label: 'Healthy Ageing', desc: 'Nutrition for longevity & wellbeing' },
        { icon: 'light-bulb-solid', label: 'Preventive Nutrition', desc: 'Diet-driven disease prevention' },
        { icon: 'vial', label: 'Bioactives & Nutraceuticals', desc: 'Functional compounds & supplements' },
      ],
      insights: [
        {
          kind: 'Pulse',
          icon: 'wave-square',
          title: 'Monthly snapshot of the transition',
          summary: 'A regular pulse on the signals and momentum shaping food, nutrition and health.',
          meta: 'View →'
        },
        {
          kind: 'Radar',
          icon: 'chart-bubble',
          title: 'Emerging players and trends',
          summary: 'Discover the emerging organisations and innovations at the intersection of food and health.',
          meta: 'View →'
        },
        {
          kind: 'Deep Dive',
          icon: 'news-paper-solid',
          title: 'In-depth food & health reports',
          summary: 'Deep dive analyses exploring the science and dynamics driving healthier food systems.',
          meta: 'Read reports'
        },
      ],
      partners: ['Foodvalley'],
      // TODO: set the partner portfolio id for the Health & Functional Food transition
      partnersPortfolio: 'lMk1BJ6xRK',
      faq: [
        {
          q: 'What is Health & Functional Food?',
          a: 'It is the transition towards food systems that actively support health and prevention — combining nutrition science, functional ingredients and innovation to improve wellbeing.'
        },
        {
          q: 'What is personalised nutrition?',
          a: 'Personalised nutrition tailors dietary advice and products to an individual’s needs, using data such as health, lifestyle and biological markers.'
        },
        {
          q: 'Why is gut health important?',
          a: 'The gut microbiome influences digestion, immunity and overall health, making it a key focus for functional foods and preventive nutrition.'
        },
        {
          q: 'What are functional ingredients?',
          a: 'Functional ingredients are components such as fibres, probiotics and bioactives that provide health benefits beyond basic nutrition.'
        },
        {
          q: 'Which organisations are active in this transition?',
          a: 'The ecosystem includes food companies, ingredient suppliers, healthcare organisations, biotech innovators, researchers, investors and ecosystem builders.'
        },
      ],
      mapUrl: 'https://platform.foodleap.com/ecosystem-maps/16/embed?apiKey=ds_live_LivhKbBN600tUdKAXqjj',
      mapComingSoon: false,
      image: '/images/static-home-page/foodleap/transition-maps/health.png',
      blobClass: 'ftl-blob-yellow',
      pillClass: 'bg-secondary-yellow/10 text-secondary-yellow',
      ctaBtnClass: 'bg-secondary-yellow text-primary-slate hover:opacity-90',
      detailBorderClass: 'border-secondary-yellow/20',
      iconBgActive: 'bg-secondary-yellow/20',
      iconColorActive: 'text-secondary-yellow',
      headerBgClass: 'bg-secondary-yellow/5',
      borderClass: 'border-secondary-yellow/20 hover:border-secondary-yellow/50',
      dividerClass: 'border-secondary-yellow/10',
      labelColor: 'text-secondary-yellow',
      linkColor: 'text-secondary-yellow',
    },
    {
      slug: 'regenerative',
      label: 'Nature Positive',
      shortTitle: 'Soil, water & biodiversity',
      title: 'Nature Positive Food Systems',
      description: 'Explore the organisations, innovations and insights helping food systems contribute to healthy ecosystems. Discover the ecosystem driving the transition towards more regenerative and resilient food systems.',
      shortDesc: 'Explore the organisations and innovations helping food systems contribute to healthy ecosystems.',
      icon: 'eco',
      intro: {
        heading: 'Introduction',
        paragraphs: [
          'Healthy food systems depend on healthy ecosystems.',
          'As biodiversity declines and climate pressures increase, organisations across the food value chain are exploring ways to restore natural systems while maintaining productive agriculture and food production.',
          'The Nature Positive Food Systems transition brings together farmers, food companies, researchers, technology providers, investors and policymakers working towards more resilient food systems that support biodiversity, soil health and ecosystem resilience.',
        ],
      },
      stats: [
        { value: '80+', label: 'organisations' },
        { value: '9', label: 'countries' },
        { value: '15', label: 'domains' },
      ],
      facts: [
        { icon: 'users', value: '80+', label: 'organisations' },
        { icon: 'circle-nodes', value: '6', label: 'stakeholder groups' },
        { icon: 'light-bulb-solid', value: '15', label: 'innovation domains' },
        { icon: 'globe', value: '9', label: 'countries' },
        { icon: 'wave-square', value: '4,000+', label: 'signals monitored' },
      ],
      innovationAreas: [
        { icon: 'eco', label: 'Regenerative Agriculture', desc: 'Farming that restores soil & ecosystems' },
        { icon: 'bar-chart', label: 'Soil Health', desc: 'Soil carbon, fertility & structure' },
        { icon: 'chart-bubble', label: 'Biodiversity', desc: 'Habitat, species & ecosystem diversity' },
        { icon: 'wave-square', label: 'Water Stewardship', desc: 'Water quality & efficient use' },
        { icon: 'light-bulb-solid', label: 'Carbon Farming', desc: 'Carbon sequestration & credits' },
        { icon: 'lab', label: 'Agroecology', desc: 'Ecological farming systems' },
        { icon: 'vial', label: 'Nature-Based Solutions', desc: 'Restoring natural systems at scale' },
      ],
      insights: [
        {
          kind: 'Pulse',
          icon: 'wave-square',
          title: 'Monthly snapshot of the transition',
          summary: 'A regular pulse on the signals and momentum shaping nature-positive food systems.',
          meta: 'View →'
        },
        {
          kind: 'Radar',
          icon: 'chart-bubble',
          title: 'Emerging players and trends',
          summary: 'Discover the emerging organisations and innovations driving nature-positive food systems.',
          meta: 'View →'
        },
        {
          kind: 'Deep Dive',
          icon: 'news-paper-solid',
          title: 'In-depth nature-positive reports',
          summary: 'Deep dive analyses exploring regenerative practices and ecosystem resilience.',
          meta: 'Read reports'
        },
      ],
      partners: ['Foodvalley'],
      // TODO: set the partner portfolio id for the Nature Positive Food Systems transition
      partnersPortfolio: 'aAVbzb9Vmr',
      faq: [
        {
          q: 'What are Nature Positive Food Systems?',
          a: 'They are food systems that actively restore and regenerate nature — supporting biodiversity, soil health and ecosystem resilience while producing food.'
        },
        {
          q: 'What is regenerative agriculture?',
          a: 'Regenerative agriculture is a set of farming practices that rebuild soil health, increase biodiversity and improve water and carbon cycles.'
        },
        {
          q: 'Why is biodiversity important for food systems?',
          a: 'Biodiversity underpins healthy ecosystems, pollination, soil fertility and resilience, making it essential for long-term food production.'
        },
        {
          q: 'What is carbon farming?',
          a: 'Carbon farming uses agricultural practices that capture and store carbon in soils and vegetation, often generating carbon credits.'
        },
        {
          q: 'Which organisations are active in this transition?',
          a: 'The ecosystem includes farmers, food companies, researchers, technology providers, investors and policymakers working on nature-positive food production.'
        },
      ],
      mapUrl: 'https://platform.foodleap.com/ecosystem-maps/17/embed?apiKey=ds_live_Ugbc2ehd630hfUnaUUGC',
      // Map not ready yet — show the coming soon banner instead of the live map.
      mapComingSoon: true,
      image: '/images/static-home-page/foodleap/transition-maps/nature.png',
      blobClass: 'ftl-blob-vgreen',
      pillClass: 'bg-secondary-vibrant-green/20 text-primary-slate',
      ctaBtnClass: 'bg-secondary-vibrant-green text-primary-slate hover:opacity-90',
      detailBorderClass: 'border-secondary-vibrant-green/30',
      iconBgActive: 'bg-secondary-vibrant-green/30',
      iconColorActive: 'text-primary-slate',
      headerBgClass: 'bg-secondary-vibrant-green/10',
      borderClass: 'border-secondary-vibrant-green/30 hover:border-secondary-vibrant-green/50',
      dividerClass: 'border-secondary-vibrant-green/20',
      labelColor: 'text-primary-slate',
      linkColor: 'text-primary-slate',
    },
  ]

  export default defineComponent({
    name: 'StaticHomeFoodleapTransitionLanding',
    components: { SimplifiedFooterFoodleapNew, SamergoActorCard, Icon, Carousel, Slide, Navigation },
    mixins: [CarouselMixin],

    data () {
      return {
        openFaq: 0,
        partnerActors: [],
        partnerActorsLoading: false,
      }
    },

    computed: {
      slug () {
        return this.$route.params.slug
      },
      transitionIndex () {
        return ALL_TRANSITIONS.findIndex(t => t.slug === this.slug)
      },
      transition () {
        return ALL_TRANSITIONS[this.transitionIndex] || null
      },
      otherTransitions () {
        const pickCardFacts = (facts = []) => {
          const picked = ['organisations', 'countries', 'domains']
            .map(w => facts.find(f => (f.label || '').includes(w)))
            .filter(Boolean)
          return picked.length === 3 ? picked : facts.slice(0, 3)
        }

        return ALL_TRANSITIONS
          .filter(t => t.slug !== this.slug)
          .map(t => ({
            ...t,
            headerBg: t.headerBgClass,
            desc: t.shortDesc,
            cardFacts: pickCardFacts(t.facts),
            // Match the homepage cards: use the transition's accent (green for Nature Positive)
            // instead of the slate fallback for the card's label/link.
            labelColor: t.labelColor === 'text-primary-slate' ? 'text-primary-green' : t.labelColor,
            linkColor: t.linkColor === 'text-primary-slate' ? 'text-primary-green' : t.linkColor,
          }))
      },
      heroStats () {
        return (this.transition?.facts || []).slice(0, 4)
      },
      partnersPortfolio () {
        return this.transition?.partnersPortfolio || ''
      },
    },

    watch: {
      slug () {
        this.openFaq = 0
        this.initObservers()
        this.fetchPartnerActors()
        window.scrollTo({ top: 0, behavior: 'smooth' })
      },
    },

    methods: {
      onAddOrganisation () {
        trackFoodleapCta('Transition Landing', 'Add your organisation', this.transition?.label)
        this.$router.push('/register')
      },
      onBecomePartner () {
        trackFoodleapCta('Transition Landing', 'Become a partner', this.transition?.label)
        this.$router.push('/register')
      },
      async fetchPartnerActors () {
        const portfolio = this.partnersPortfolio
        this.partnerActors = []
        if (!portfolio) return
        this.partnerActorsLoading = true
        try {
          const res = await fetchActors({ portfolio, limit: PARTNERS_LIMIT, offset: 0, order: '-created_at' })
          this.partnerActors = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : [])
        } catch {
          this.partnerActors = []
        } finally {
          this.partnerActorsLoading = false
        }
      },
      insightUrl (insight) {
        switch (insight.kind) {
          case 'Pulse':
            return '/transition-pulse-report'
          case 'Radar':
            return '/innovation-radar-report'
          case 'Deep Dive':
            return '/deep-dive-report'
          default:
            return null
        }
      },
      onInsightClick (insight) {
        const url = this.insightUrl(insight)
        if (!url) return
        trackFoodleapCta('Transition Landing', 'Insight: ' + insight.kind, this.transition?.label)
        this.$router.push(url)
      },
      scrollTo (refName) {
        const el = this.$refs[refName]
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      },
      toggleFaq (index) {
        this.openFaq = this.openFaq === index ? null : index
      },
      initObservers () {
        if (this._observer) this._observer.disconnect()
        this._observer = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                entry.target.classList.add('is-visible')
                this._observer.unobserve(entry.target)
              }
            })
          },
          { threshold: 0.1 },
        )
        this.$nextTick(() => {
          this.$el.querySelectorAll('.animate-on-scroll').forEach(el => {
            el.classList.remove('is-visible')
            this._observer.observe(el)
          })
        })
      },
    },

    mounted () {
      this._scrollTracker = createScrollDepthTracker('Foodleap Transition Landing')
      this._scrollTracker.start()
      this.initObservers()
      this.fetchPartnerActors()
    },

    beforeUnmount () {
      if (this._observer) this._observer.disconnect()
      if (this._scrollTracker) this._scrollTracker.stop()
    },
  })
</script>

<style lang="scss" scoped>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  .foodleap-transition-landing {
    font-family: 'Inter', sans-serif;
    background-color: #ffffff;
    color: #21244A;

    $ps: #21244A;
    $pg: #5CCC61;
    $blg: #D9E2DA;
    $yellow: #FFC83D;
    $blue: #70C1FF;
    $vgreen: #B5F167;
    $purple: #C084FC;

    // ── Scroll-reveal ──────────────────────────────────────────────────────────
    .animate-on-scroll {
      opacity: 0;
      transform: translateY(20px);
      transition: opacity 0.6s ease, transform 0.6s ease;

      &.is-visible {
        opacity: 1;
        transform: translateY(0);
      }

      &.delay-1 {
        transition-delay: 0.1s;
      }

      &.delay-2 {
        transition-delay: 0.2s;
      }
    }

    // ── Hero ───────────────────────────────────────────────────────────────────
    .ftl-hero {
      background: linear-gradient(180deg, #f6f9f6 0%, #ffffff 100%);
    }

    .ftl-num {
      font-size: 2rem;
      line-height: 1;
    }

    .ftl-stat-panel {
      background: rgba(255, 255, 255, 0.7);
      backdrop-filter: blur(8px);
      box-shadow: 0 8px 40px rgba(33, 36, 74, 0.05);
    }

    .ftl-blob-green {
      background: $pg;
    }

    .ftl-blob-blue {
      background: $blue;
    }

    .ftl-blob-yellow {
      background: $yellow;
    }

    .ftl-blob-vgreen {
      background: $vgreen;
    }

    .ftl-blob-purple {
      background: $purple;
    }

    .ftl-blob-2 {
      background: $blue;
    }

    // ── Ghost / secondary button ─────────────────────────────────────────────
    .ftl-btn-ghost {
      border: 1.5px solid rgba(33, 36, 74, 0.12);
      color: $ps;
      background: #ffffff;
      transition: all 0.2s;

      &:hover {
        border-color: rgba(33, 36, 74, 0.3);
        transform: translateY(-1px);
      }
    }

    // ── Cards (intro / facts / areas / insights / partners) ──────────────────
    .ftl-intro-card,
    .ftl-partners-card {
      background: #fbfdfb;
    }

    .ftl-partners-carousel {
      // stretch slides so cards share an equal height per row
      :deep(.carousel__track) {
        align-items: stretch;
      }

      :deep(.carousel__slide) {
        align-items: stretch;
      }
    }

    .ftl-fact-card,
    .ftl-insight-card {
      transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s;

      &:hover {
        transform: translateY(-3px);
        box-shadow: 0 12px 32px rgba(33, 36, 74, 0.07);
      }
    }

    // ── Map ────────────────────────────────────────────────────────────────────
    .ftl-map-wrap {
      box-shadow: 0 8px 40px rgba(33, 36, 74, 0.06);
    }

    // ── FAQ ────────────────────────────────────────────────────────────────────
    .ftl-faq-item button {
      background: none;
      cursor: pointer;
    }

    // ── Other transition cards ─────────────────────────────────────────────────
    .ftl-other-card {
      transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s;

      &:hover {
        transform: translateY(-3px);
        box-shadow: 0 12px 32px rgba(33, 36, 74, 0.08);
        text-decoration: none;
      }
    }

    // ── Bottom CTA ─────────────────────────────────────────────────────────────
    .ftl-cta-bg {
      background-color: $ps;
    }

    .ftl-btn-green {
      background-color: $pg;
      color: $ps;
      transition: opacity 0.2s;

      &:hover {
        opacity: 0.9;
      }

      svg {
        transition: transform 0.2s;
      }

      &:hover svg {
        transform: translateX(3px);
      }
    }

    .ftl-btn-outline {
      border: 2px solid rgba(255, 255, 255, 0.25);
      color: rgba(255, 255, 255, 0.75);
      transition: all 0.2s;

      &:hover {
        border-color: rgba(255, 255, 255, 0.6);
        color: #ffffff;
      }
    }

    // ── Color utilities (scoped) ───────────────────────────────────────────────
    .text-primary-slate {
      color: $ps;
    }

    .text-primary-green {
      color: $pg;
    }

    .text-secondary-blue {
      color: $blue;
    }

    .text-secondary-yellow {
      color: $yellow;
    }

    .text-secondary-purple {
      color: $purple;
    }

    .text-primary-slate\/20 {
      color: rgba(33, 36, 74, 0.2);
    }

    .text-primary-slate\/35 {
      color: rgba(33, 36, 74, 0.35);
    }

    .text-primary-slate\/40 {
      color: rgba(33, 36, 74, 0.4);
    }

    .text-primary-slate\/50 {
      color: rgba(33, 36, 74, 0.5);
    }

    .text-primary-slate\/60 {
      color: rgba(33, 36, 74, 0.6);
    }

    .text-primary-slate\/70 {
      color: rgba(33, 36, 74, 0.7);
    }

    .bg-primary-green\/5 {
      background-color: rgba(92, 204, 97, 0.05);
    }

    .bg-primary-green\/10 {
      background-color: rgba(92, 204, 97, 0.10);
    }

    .bg-primary-green\/20 {
      background-color: rgba(92, 204, 97, 0.20);
    }

    .bg-secondary-blue\/5 {
      background-color: rgba(112, 193, 255, 0.05);
    }

    .bg-secondary-blue\/10 {
      background-color: rgba(112, 193, 255, 0.10);
    }

    .bg-secondary-blue\/20 {
      background-color: rgba(112, 193, 255, 0.20);
    }

    .bg-secondary-yellow\/5 {
      background-color: rgba(255, 200, 61, 0.05);
    }

    .bg-secondary-yellow\/10 {
      background-color: rgba(255, 200, 61, 0.10);
    }

    .bg-secondary-yellow\/20 {
      background-color: rgba(255, 200, 61, 0.20);
    }

    .bg-secondary-vibrant-green\/5 {
      background-color: rgba(181, 241, 103, 0.05);
    }

    .bg-secondary-vibrant-green\/10 {
      background-color: rgba(181, 241, 103, 0.10);
    }

    .bg-secondary-vibrant-green\/20 {
      background-color: rgba(181, 241, 103, 0.20);
    }

    .bg-secondary-vibrant-green\/30 {
      background-color: rgba(181, 241, 103, 0.30);
    }

    .bg-secondary-purple\/5 {
      background-color: rgba(192, 132, 252, 0.05);
    }

    .bg-secondary-purple\/10 {
      background-color: rgba(192, 132, 252, 0.10);
    }

    .bg-secondary-purple\/20 {
      background-color: rgba(192, 132, 252, 0.20);
    }

    .bg-brand-light-grey\/60 {
      background-color: rgba(217, 226, 218, 0.60);
    }

    .border-primary-green\/10 {
      border-color: rgba(92, 204, 97, 0.10);
    }

    .border-primary-green\/20 {
      border-color: rgba(92, 204, 97, 0.20);
    }

    .border-secondary-blue\/10 {
      border-color: rgba(112, 193, 255, 0.10);
    }

    .border-secondary-blue\/20 {
      border-color: rgba(112, 193, 255, 0.20);
    }

    .border-secondary-yellow\/10 {
      border-color: rgba(255, 200, 61, 0.10);
    }

    .border-secondary-yellow\/20 {
      border-color: rgba(255, 200, 61, 0.20);
    }

    .border-secondary-vibrant-green\/10 {
      border-color: rgba(181, 241, 103, 0.10);
    }

    .border-secondary-vibrant-green\/20 {
      border-color: rgba(181, 241, 103, 0.20);
    }

    .border-secondary-vibrant-green\/30 {
      border-color: rgba(181, 241, 103, 0.30);
    }

    .border-secondary-purple\/10 {
      border-color: rgba(192, 132, 252, 0.10);
    }

    .border-secondary-purple\/20 {
      border-color: rgba(192, 132, 252, 0.20);
    }

    .hover\:border-primary-green\/50:hover {
      border-color: rgba(92, 204, 97, 0.50);
    }

    .hover\:border-secondary-blue\/50:hover {
      border-color: rgba(112, 193, 255, 0.50);
    }

    .hover\:border-secondary-yellow\/50:hover {
      border-color: rgba(255, 200, 61, 0.50);
    }

    .hover\:border-secondary-vibrant-green\/50:hover {
      border-color: rgba(181, 241, 103, 0.50);
    }

    .hover\:border-secondary-purple\/50:hover {
      border-color: rgba(192, 132, 252, 0.50);
    }

    .bg-primary-green {
      background-color: $pg;
    }

    .bg-secondary-blue {
      background-color: $blue;
    }

    .bg-secondary-yellow {
      background-color: $yellow;
    }

    .bg-secondary-vibrant-green {
      background-color: $vgreen;
    }

    .bg-secondary-purple {
      background-color: $purple;
    }
  }
</style>
